/**
 * Trunnion AI - Top Navigation Bar
 * Displays logo, user menu, notification bell, and system health indicator.
 */

import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Shield,
  User,
  LogOut,
  Settings,
  Bell,
  ChevronDown,
  Activity,
  Search,
} from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { useNotificationStore } from '@/stores/notificationStore';
import { useAuth } from '@/hooks/useAuth';
import { useCommandPalette } from '@/hooks/useCommandPalette';
import { ROUTES } from '@/config/routes';
import { CLASSIFICATION_CONFIG } from '@/config/constants';
import { StatusDot } from '@/components/ui/StatusDot';
import { NotificationCenter } from '@/components/notifications/NotificationCenter';

export function TopNav() {
  const { user, clearanceContext } = useAuthStore();
  const { approvalQueue, unreadCount } = useNotificationStore();
  const { logout } = useAuth();
  const { open: openCommandPalette } = useCommandPalette();
  const [menuOpen, setMenuOpen] = useState(false);
  const [notificationCenterOpen, setNotificationCenterOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const clearanceLabel = clearanceContext
    ? CLASSIFICATION_CONFIG[clearanceContext.operatorClearance]?.shortLabel
    : 'U';

  const totalBadgeCount = unreadCount + approvalQueue.length;

  return (
    <>
      <nav className="flex h-14 items-center justify-between border-b border-trunnion-slate-700 bg-trunnion-slate-900 px-4">
        {/* Logo */}
        <Link to={ROUTES.DASHBOARD} className="flex items-center gap-2.5">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-trunnion-gold-500/20">
            <Shield className="h-5 w-5 text-trunnion-gold-500" />
          </div>
          <span className="text-lg font-bold text-white">
            Trunnion <span className="text-trunnion-gold-500">AI</span>
          </span>
        </Link>

        {/* Right side */}
        <div className="flex items-center gap-4">
          {/* Search Trigger */}
          <button
            onClick={openCommandPalette}
            className="flex items-center gap-2 rounded-lg border border-trunnion-slate-700 bg-trunnion-slate-800 px-3 py-1.5 text-sm text-trunnion-slate-400 hover:border-trunnion-slate-600 hover:text-trunnion-slate-300 transition-colors"
          >
            <Search className="h-3.5 w-3.5" />
            <span className="hidden md:inline">Search...</span>
            <kbd className="hidden md:inline-flex items-center rounded border border-trunnion-slate-600 bg-trunnion-slate-700 px-1.5 py-0.5 text-[10px] font-medium text-trunnion-slate-400">
              ⌘K
            </kbd>
          </button>

          {/* System Health */}
          <div className="flex items-center gap-1.5 text-sm text-trunnion-slate-300">
            <Activity className="h-4 w-4" />
            <StatusDot status="healthy" />
            <span className="hidden sm:inline">System OK</span>
          </div>

          {/* Notification Bell */}
          <button
            onClick={() => setNotificationCenterOpen(!notificationCenterOpen)}
            className="relative flex items-center justify-center rounded-lg p-2 text-trunnion-slate-300 hover:bg-trunnion-slate-800 hover:text-white transition-colors"
            aria-label="Toggle notification center"
          >
            <Bell className="h-5 w-5" />
            {totalBadgeCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-trunnion-gold-500 px-1 text-[10px] font-bold text-trunnion-slate-950">
                {totalBadgeCount > 99 ? '99+' : totalBadgeCount}
              </span>
            )}
          </button>

          {/* User Menu */}
          <div ref={menuRef} className="relative">
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-sm text-trunnion-slate-200 hover:bg-trunnion-slate-800 transition-colors"
            >
              <div className="flex h-7 w-7 items-center justify-center rounded-full bg-trunnion-gold-500/20 text-xs font-bold text-trunnion-gold-500">
                {user?.displayName?.charAt(0)?.toUpperCase() ?? 'U'}
              </div>
              <div className="hidden sm:flex flex-col items-start">
                <span className="font-medium text-white">{user?.displayName ?? 'Operator'}</span>
                <span className="text-xs text-trunnion-slate-400">{clearanceLabel}</span>
              </div>
              <ChevronDown className="h-4 w-4 text-trunnion-slate-400" />
            </button>

            {menuOpen && (
              <div className="absolute right-0 top-full mt-1 w-56 rounded-lg border border-trunnion-slate-700 bg-trunnion-slate-800 py-1 shadow-xl z-50 animate-fade-in">
                <div className="border-b border-trunnion-slate-700 px-3 py-2">
                  <p className="text-sm font-medium text-white">{user?.displayName}</p>
                  <p className="text-xs text-trunnion-slate-400">{user?.email}</p>
                  <p className="text-xs text-trunnion-slate-400 mt-0.5">
                    Role: {user?.role} | Clearance: {clearanceLabel}
                  </p>
                </div>
                <Link
                  to={ROUTES.ADMIN}
                  className="flex w-full items-center gap-2 px-3 py-2 text-sm text-trunnion-slate-200 hover:bg-trunnion-slate-700"
                  onClick={() => setMenuOpen(false)}
                >
                  <Settings className="h-4 w-4" />
                  Admin Settings
                </Link>
                <Link
                  to={ROUTES.AUDIT}
                  className="flex w-full items-center gap-2 px-3 py-2 text-sm text-trunnion-slate-200 hover:bg-trunnion-slate-700"
                  onClick={() => setMenuOpen(false)}
                >
                  <User className="h-4 w-4" />
                  Audit Log
                </Link>
                <hr className="border-trunnion-slate-700" />
                <button
                  onClick={() => {
                    setMenuOpen(false);
                    logout();
                  }}
                  className="flex w-full items-center gap-2 px-3 py-2 text-sm text-red-400 hover:bg-trunnion-slate-700"
                >
                  <LogOut className="h-4 w-4" />
                  Sign Out
                </button>
              </div>
            )}
          </div>
        </div>
      </nav>

      {/* Notification Center Panel */}
      <NotificationCenter
        open={notificationCenterOpen}
        onClose={() => setNotificationCenterOpen(false)}
      />
    </>
  );
}
